import { useState, useRef, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Send, Paperclip, Clock, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../store/auth'
import { emailApi, type SendEmailPayload } from '../../api/email'
import { LeadSearchInput } from './LeadSearchInput'
import { SchedulePopover } from './SchedulePopover'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'

import type { EmailMessage } from '../../api/email'

interface Props {
  companyId: string
  onClose: () => void
  draftMessage?: EmailMessage  // when opening an existing draft
}

interface Lead { id: string; firstName: string; lastName: string; email: string }
interface FormFields { to: string; cc?: string; bcc?: string; subject: string; body: string }

export function ComposePane({ companyId, onClose, draftMessage }: Props) {
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const [linkedLead, setLinkedLead] = useState<Lead | null>(null)
  const [showCc, setShowCc] = useState(false)
  const [showBcc, setShowBcc] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<string | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const draftIdRef = useRef<string | null>(draftMessage?.id ?? null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isOwnerOrAdmin = user?.role !== 'sales_user'

  const isDraftMode = !!draftMessage

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormFields>({
    defaultValues: {
      to: (draftMessage?.toAddresses ?? [])[0] ?? '',
      subject: draftMessage?.subject ?? '',
      body: draftMessage?.bodyText ?? '',
    },
  })

  const scheduleDraftSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      const vals = { to: watch('to'), subject: watch('subject'), body: watch('body') }
      if (!vals.body?.trim() && !vals.subject?.trim() && !vals.to?.trim()) return
      const dto: Partial<SendEmailPayload> = {
        toAddresses: isOwnerOrAdmin ? (vals.to ? [vals.to] : []) : (linkedLead?.email ? [linkedLead.email] : []),
        subject: vals.subject || '',
        bodyText: vals.body || '',
        refId: linkedLead?.id,
        refType: linkedLead ? 'lead' : undefined,
      }
      if (draftIdRef.current) {
        emailApi.updateDraft(companyId, draftIdRef.current, dto).catch(() => {})
      } else {
        emailApi.saveDraft(companyId, dto).then(r => {
          draftIdRef.current = r.data.id
          qc.invalidateQueries({ queryKey: ['inbox', companyId] })
        }).catch(() => {})
      }
    }, 2000)
  }

  useEffect(() => {
    const { unsubscribe } = watch(() => scheduleDraftSave())
    return unsubscribe
  }, [watch, linkedLead])

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  const mutation = useMutation({
    mutationFn: async (data: FormFields) => {
      if (!isOwnerOrAdmin && !linkedLead) {
        throw new Error('Please select a lead to link this email to.')
      }
      const dto: SendEmailPayload = {
        toAddresses: isOwnerOrAdmin ? [data.to] : [linkedLead!.email],
        ccAddresses: data.cc ? [data.cc] : undefined,
        bccAddresses: data.bcc ? [data.bcc] : undefined,
        subject: data.subject,
        bodyText: data.body,
        sendAt: scheduledAt ?? undefined,
        refId: linkedLead?.id,
        refType: linkedLead ? 'lead' : undefined,
      }
      const hasRef = dto.refId && dto.refType
      if (!hasRef && isOwnerOrAdmin) {
        return emailApi.sendUnlinked(companyId, dto, files)
      }
      return emailApi.send(companyId, dto.refType!, dto.refId!, dto, files)
    },
    onSuccess: () => {
      toast.success(scheduledAt ? 'Email scheduled' : 'Email sent')
      if (draftIdRef.current) {
        emailApi.deleteDraft(companyId, draftIdRef.current).catch(() => {})
        draftIdRef.current = null
      }
      qc.invalidateQueries({ queryKey: ['inbox', companyId] })
      onClose()
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? err?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to send email')
    },
  })

  const confirmDiscardAction = () => {
    if (draftIdRef.current) {
      emailApi.deleteDraft(companyId, draftIdRef.current).catch(() => {})
      draftIdRef.current = null
    }
    qc.invalidateQueries({ queryKey: ['inbox', companyId] })
    setConfirmDiscard(false)
    onClose()
  }

  return (
    <div className="flex flex-col flex-1 bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-800">
          {isDraftMode ? 'Edit Draft' : scheduledAt ? `Scheduled: ${new Date(scheduledAt).toLocaleString()}` : 'New Message'}
        </h3>
        <button type="button" onClick={onClose} className="btn btn-ghost btn-sm p-1.5 text-gray-500">
          <X className="w-4 h-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="flex flex-col flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {isOwnerOrAdmin && (
            <div>
              <label className="label">To *</label>
              <input {...register('to', { required: 'Required' })} type="email" className="input" />
              {errors.to && <p className="text-xs text-red-500 mt-1">{errors.to.message}</p>}
            </div>
          )}

          <div className="flex gap-3">
            {!showCc && <button type="button" onClick={() => setShowCc(true)} className="text-xs text-primary-600 hover:underline">+ Add CC</button>}
            {!showBcc && <button type="button" onClick={() => setShowBcc(true)} className="text-xs text-primary-600 hover:underline">+ Add BCC</button>}
          </div>
          {showCc && <div><label className="label">CC</label><input {...register('cc')} type="email" className="input" /></div>}
          {showBcc && <div><label className="label">BCC</label><input {...register('bcc')} type="email" className="input" /></div>}

          <div>
            <label className="label">
              {isOwnerOrAdmin
                ? 'Link to lead'
                : <>Send to lead<span className="text-red-500 ml-0.5">*</span></>}
            </label>
            <LeadSearchInput
              companyId={companyId}
              value={linkedLead}
              onChange={setLinkedLead}
              required={!isOwnerOrAdmin}
              placeholder="Search leads by name or email…"
              hideEmail={!isOwnerOrAdmin}
            />
            {isOwnerOrAdmin && !linkedLead && (
              <p className="text-xs text-gray-400 mt-1">Optional — leave empty to send without linking to a lead</p>
            )}
          </div>

          <div>
            <label className="label">Subject *</label>
            <input {...register('subject', { required: 'Required' })} className="input" />
          </div>

          <div>
            <label className="label">Message *</label>
            <textarea
              {...register('body', { required: 'Required' })}
              rows={10}
              className="input resize-none font-mono text-sm"
              placeholder="Write your message…"
            />
          </div>

          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs text-gray-700">
                  <Paperclip className="w-3 h-3" />
                  {f.name}
                  <button type="button" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 ml-1">×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-4 py-3 flex items-center gap-2 bg-gray-50 flex-shrink-0">
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">
            <Send className="w-3.5 h-3.5" />
            {scheduledAt ? 'Schedule' : mutation.isPending ? 'Sending…' : 'Send'}
          </button>

          <div className="relative">
            <button type="button" onClick={() => setShowSchedule(s => !s)} className="btn btn-ghost btn-sm p-1.5" title="Schedule send">
              <Clock className="w-4 h-4 text-gray-500" />
            </button>
            {showSchedule && (
              <SchedulePopover
                onSchedule={iso => { setScheduledAt(iso); setShowSchedule(false) }}
                onClose={() => setShowSchedule(false)}
              />
            )}
          </div>

          {scheduledAt && (
            <button type="button" onClick={() => setScheduledAt(null)} className="text-xs text-red-500 hover:underline">
              Remove schedule
            </button>
          )}

          <button type="button" onClick={() => fileRef.current?.click()} className="btn btn-ghost btn-sm p-1.5" title="Attach file">
            <Paperclip className="w-4 h-4 text-gray-500" />
          </button>
          <input ref={fileRef} type="file" multiple className="hidden"
            onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files ?? [])])} />

          <button type="button" onClick={() => setConfirmDiscard(true)} className="btn btn-ghost btn-sm text-red-400 hover:text-red-600 ml-auto">
            Discard draft
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={confirmDiscardAction}
        title="Discard draft?"
        message="This draft will be permanently deleted."
        confirmLabel="Discard"
      />
    </div>
  )
}
